import React, { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";

import IconButton from "../../packages/IconButton";
import WPTertiaryButton from "../../packages/WPTertiaryButton";

import { useUnsavedChanges } from "../../hooks/useUnsavedChanges";

import DupontButton from "../DupontButton";
import createNotification, { ERROR_NOTIFICATION } from "../Notifications";
import PlantMenuHeader from "../PlantMenuHeader";
import RevisitTour from "../RevisitTour";
import UtilityCostsDialog from "../UtilityCosts/UtilityCostsDialog";
import UserProfile from "./UserProfile";

import {
  APP_ROUTES,
  FEATURE_SCOPE,
  LOGOUT_MARKER_KEY,
  LOGOUT_RETURN_URL,
} from "../../constants/global.constants";

import { ArrowLeftIcon, DuPontTMLogo, ROAdvisorLogo } from "../../assets/icons";

import { combineClassNames } from "../../utils/appUtils";

import { updateLanguageInHeaders } from "../../network/api";
import { API_URLS } from "../../network/appUrls";
import AxiosInstance from "../../network/axiosInstance";
import { saveUser } from "../../network/cipAPI";

import {
  clearAuthSession,
  storeUserProfileData,
  storeuserRole,
} from "../../Store/TemplateStore/template.action";

import "./index.scss";
const basePath = API_URLS.basePath;

const Header = ({
  showPlantMenu,
  activeStep,
  isLoading = false,
  disableActions = false,
  onPlantUtilityCostsSaved,
}) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { setShowDialog, allowActionRef, pendingActionRef, hasChangesRef } =
    useUnsavedChanges();

  const {
    accessUserPlant,
    userName,
    userRole,
    userEmail,
    companyName,
    country,
    currency,
    volume,
    conversion_rate,
  } = useSelector((state) => state);

  const [utilityCostsOpen, setUtilityCostsOpen] = useState(false);
  const [utilityCostsScope, setUtilityCostsScope] = useState(
    FEATURE_SCOPE.USER,
  );
  const [showRevisitHint, setShowRevisitHint] = useState(false);
  const [forceUserProfileOpen, setForceUserProfileOpen] = useState(false);
  const [tourRestartTrigger, setTourRestartTrigger] = useState(0);

  const changeLanguage = async (lng) => {
    i18n.changeLanguage(lng);
    updateLanguageInHeaders(lng);
  };

  const payload = {
    email: userEmail,
    company: companyName,
    country: country,
    role: null,
    industry: null,
  };

  const saveUserDetails = async (payload) => {
    try {
      const response = await saveUser(payload);
      const { user_role } = response;
      dispatch(storeuserRole(`${user_role}`));
      dispatch(storeUserProfileData(response));
    } catch (error) {
      createNotification(
        ERROR_NOTIFICATION,
        t("notificationStrings.somethingWrong"),
        "",
      );
    }
  };

  const onHomeClicked = () => {
    // re fetching user details to get user role, bcz on click of home we are navigating to plants page and in plants page we are checking resetting app's redux store
    saveUserDetails(payload);
    const path = `${basePath}plants`;
    navigate(path);
  };

  const handleBackToUserManagement = () => {
    dispatch({ type: "STORE_ACCESS_USER_PLANT", payload: false });
    navigate(`${basePath}${APP_ROUTES.USER_MANAGEMENT}`);
  };

  const handleManageAccess = () => {
    navigate(`${basePath}${APP_ROUTES.ADMIN_PORTAL}`);
  };

  const handleManageUser = () => {
    navigate(`${basePath}${APP_ROUTES.USER_MANAGEMENT}`);
  };

  const handleManageLicense = () => {
    navigate(`${basePath}${APP_ROUTES.LICENSE_MANAGEMENT}`);
  };

  const openUtilityCosts = (scope) => {
    setUtilityCostsScope(scope);
    setUtilityCostsOpen(true);
  };

  const handleUserUtilityCosts = () => {
    openUtilityCosts(FEATURE_SCOPE.USER);
  };

  const handleProjectUtilityCosts = () => {
    openUtilityCosts(FEATURE_SCOPE.PLANT);
  };

  const handleUtilityCostsClose = () => {
    setUtilityCostsOpen(false);
  };

  const isPlantScope = utilityCostsScope === FEATURE_SCOPE.PLANT;
  const isAddOrEditPlantPage =
    location.pathname.includes("addPlant") ||
    location.pathname.includes("editPlant");

  const shouldEnablePendingUnitConversion =
    isPlantScope && isAddOrEditPlantPage;

  const isPathIncluded = (paths) => {
    return Array.isArray(paths)
      ? paths.some((path) => location.pathname.includes(path))
      : location.pathname.includes(paths);
  };

  const handleAvatarHover = () => {
    saveUserDetails(payload);
  };

  const handleRevisitTourClick = () => {
    setShowRevisitHint(false);
    setForceUserProfileOpen(false);
    setTourRestartTrigger((prev) => prev + 1);
  };

  const getSalesforceLogoutUrl = () => {
    const postLogoutUrl = LOGOUT_RETURN_URL;
    const salesforceAuthUrl = process.env.REACT_APP_AUTH_ENDPOINT;

    if (!postLogoutUrl) {
      return null;
    }

    if (!salesforceAuthUrl) {
      return postLogoutUrl;
    }

    const salesforceOrigin = new URL(salesforceAuthUrl).origin;

    return `${salesforceOrigin}/secur/logout.jsp?retUrl=${encodeURIComponent(
      postLogoutUrl,
    )}`;
  };

  const performSignOut = useCallback(() => {
    const salesforceLogoutUrl = getSalesforceLogoutUrl();
    if (!salesforceLogoutUrl) {
      createNotification(
        ERROR_NOTIFICATION,
        t("notificationStrings.somethingWrong"),
      );
      return;
    }

    dispatch(clearAuthSession());

    if (AxiosInstance?.defaults?.headers?.common) {
      delete AxiosInstance.defaults.headers.common.Authorization;
    }

    localStorage.setItem(LOGOUT_MARKER_KEY, "true");
    sessionStorage.clear();
    window.location.replace(salesforceLogoutUrl);
  }, [dispatch, t]);

  const handleSignOut = () => {
    if (hasChangesRef.current && allowActionRef.current === false) {
      setShowDialog(true);
      pendingActionRef.current = performSignOut;
      return;
    }

    performSignOut();
  };

  return (
    <>
      <div className="header-container">
        <div className="header-left-section">
          {accessUserPlant && (
            <IconButton
              tooltip={t("addNewPlant.backUserManagement")}
              placement="bottom"
            >
              <WPTertiaryButton
                icon={<ArrowLeftIcon />}
                className="back-user-management-btn"
                onClick={handleBackToUserManagement}
              />
            </IconButton>
          )}
          <DuPontTMLogo
            onClick={disableActions ? undefined : onHomeClicked}
            className={combineClassNames(
              "header-dupont-logo",
              disableActions && "header-dupont-logo--disabled",
            )}
          />
          {/* apply disable actions to this logo as well when click action added here in future*/}
          <ROAdvisorLogo />
          <div className="header-title">{t("header.title")}</div>
        </div>
        <div className="header-right-section">
          {!isPathIncluded(["messagePage", "requestAccess"]) && userEmail && (
            <>
              {showPlantMenu && (
                <RevisitTour
                  showtooltipAfterTour={showRevisitHint}
                  onClick={handleRevisitTourClick}
                />
              )}
              <DupontButton
                type="tertiary"
                customClass="lang-btn"
                onClick={() => changeLanguage("en")}
                disabled={activeStep && activeStep > 0}
              >
                en
              </DupontButton>
              <DupontButton
                type="tertiary"
                customClass="lang-btn chinese-btn"
                onClick={() => changeLanguage("zh")}
                disabled={activeStep && activeStep > 0}
              >
                中文
              </DupontButton>
              <UserProfile
                userName={userName}
                userEmail={userEmail}
                userRole={userRole}
                onManageAccess={handleManageAccess}
                onManageUser={handleManageUser}
                onManageLicense={handleManageLicense}
                onSignOut={handleSignOut}
                isPathIncluded={isPathIncluded}
                onAvatarHover={handleAvatarHover}
                onUtilityCosts={handleUserUtilityCosts}
                isForcedOpen={forceUserProfileOpen}
                disableActions={disableActions}
              />
            </>
          )}
        </div>
        {utilityCostsOpen && (
          <UtilityCostsDialog
            open
            scope={utilityCostsScope}
            currency={currency}
            volume={volume}
            conversionRate={conversion_rate}
            enablePendingUnitConversion={shouldEnablePendingUnitConversion}
            onUtilityCostsSaved={onPlantUtilityCostsSaved}
            onClose={handleUtilityCostsClose}
          />
        )}
      </div>
      {showPlantMenu && (
        <PlantMenuHeader
          onCostsClick={handleProjectUtilityCosts}
          setShowRevisitHint={setShowRevisitHint}
          setForceUserProfileOpen={setForceUserProfileOpen}
          tourRestartTrigger={tourRestartTrigger}
          currentPage={location.pathname}
          isLoading={isLoading}
        />
      )}
    </>
  );
};

export default Header;
